import { Link } from "react-router-dom";
import type { ArchiveItem } from "@/types/archive";

type Props = {
  archive: ArchiveItem;
  layout?: "grid" | "row";
};

export default function ArchiveCard({ archive, layout = "grid" }: Props) {
  if (layout === "row") {
    return (
      <article className="group flex flex-col sm:flex-row gap-4 md:gap-6 py-6 border-b border-background-300">
        <Link
          to={`/archive/${archive.slug}`}
          className="w-full sm:w-56 md:w-64 h-40 sm:h-36 shrink-0 rounded-lg overflow-hidden border border-background-300"
        >
          <img
            src={archive.cover}
            alt={`${archive.title} 档案配图`}
            title={`${archive.title} - 黑料网红考古档案`}
            className="w-full h-full object-top group-hover:scale-[1.03] transition-transform duration-500"
          />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="px-2 py-0.5 text-[11px] rounded-md bg-secondary-100 text-secondary-900">
              {archive.category}
            </span>
            <span className="text-[11px] text-foreground-500">{archive.date}</span>
            <span className="text-[11px] text-foreground-500">· {archive.readMinutes} 分钟</span>
          </div>
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 leading-snug mb-2">
            <Link to={`/archive/${archive.slug}`} className="hover:text-primary-600 transition-colors">
              {archive.title}
            </Link>
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
            {archive.summary}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {archive.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-[11px] text-foreground-500 border border-background-300 px-2 py-0.5 rounded-md">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col bg-background-100 border border-background-300 rounded-lg overflow-hidden hover:border-primary-300 transition-colors">
      <Link to={`/archive/${archive.slug}`} className="block w-full h-44 md:h-48 overflow-hidden">
        <img
          src={archive.cover}
          alt={`${archive.title} 档案配图`}
          title={`${archive.title} - 黑料网红旧言论存档`}
          className="w-full h-full object-top group-hover:scale-[1.04] transition-transform duration-500"
        />
      </Link>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="px-2 py-0.5 text-[11px] rounded-md bg-accent-100 text-accent-900">
            {archive.category}
          </span>
          <span className="text-[11px] text-foreground-500">{archive.heat}</span>
        </div>
        <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 leading-snug mb-2">
          <Link to={`/archive/${archive.slug}`} className="hover:text-primary-600 transition-colors">
            {archive.title}
          </Link>
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3 flex-1">
          {archive.summary}
        </p>
        <div className="mt-4 pt-3 border-t border-background-300 flex items-center justify-between text-[11px] text-foreground-500">
          <span>{archive.date}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {archive.views}
          </span>
        </div>
      </div>
    </article>
  );
}